import {
    JsonSchema,
    JsonSchemaArray,
    JsonSchemaObject,
    JsonSchemaOrRef,
    OpenApiOperation,
} from '../api/documentation.api';

export function getSchemaDetailsHeader(name: string, schema: JsonSchema): string {
    const suffix =
        'enum' in schema ? ' Enum' : 'allOf' in schema || schema.type === 'object' ? ' Object' : '';
    return name + suffix;
}

export function getSchemaNameFromRef(ref: string): string {
    return ref.replace('#/components/schemas/', '');
}

/**
 * Gets the collection of schemas that are used by an operation.
 */
export function getReferencedSchemas(
    operation: OpenApiOperation,
    schemas: { [name: string]: JsonSchema },
): { name: string; schema: JsonSchema }[] {
    const map = new Map<string, JsonSchema>();

    const addSchema = (schema: JsonSchemaOrRef) => {
        if ('$ref' in schema) {
            const name = getSchemaNameFromRef(schema.$ref);
            if (!map.has(name)) {
                const referencedSchema = schemas[name];
                map.set(name, referencedSchema);
                addSchema(referencedSchema);
            }
        } else if ('allOf' in schema) {
            schema.allOf!.forEach(addSchema);
        } else if ('anyOf' in schema) {
            schema.anyOf!.forEach(addSchema);
        } else if ('oneOf' in schema) {
            schema.oneOf!.forEach(addSchema);
        } else if (isArraySchema(schema)) {
            addSchema(schema.items);
        } else if (schema.type === 'object' && schema.properties) {
            Object.values(schema.properties).forEach(addSchema);
            if (schema.discriminator) {
                Object.values(schema.discriminator.mapping).forEach(ref =>
                    addSchema({ $ref: ref }),
                );
            }
        }
    };

    operation.parameters.forEach(parameter => addSchema(parameter.schema));
    if (operation.requestBody) {
        Object.values(operation.requestBody.content).forEach(mediaType => {
            addSchema(mediaType.schema);
        });
    }
    Object.values(operation.responses).forEach(response => {
        if (response.content) {
            Object.values(response.content).forEach(mediaType => {
                addSchema(mediaType.schema);
            });
        }
    });

    const entries = Array.from(map.entries()).map(([name, schema]) => ({ name, schema }));
    entries.sort(({ name: name1 }, { name: name2 }) => name1.localeCompare(name2));
    return entries;
}

export function isArraySchema(schema: JsonSchema): schema is JsonSchemaArray {
    return schema.type === 'array' || (Array.isArray(schema.type) && schema.type[0] === 'array');
}

/**
 * Flattens an object schema composed of `allOf` subschemas into a single object schema.
 */
export function flattenObjectSchema(
    name: string,
    allOf: JsonSchemaOrRef[],
    resolveSchemaRef: (ref: string) => JsonSchema,
): JsonSchemaObject {
    const properties: {
        [name: string]: JsonSchemaOrRef;
    } = {};
    const required: string[] = [];

    for (const subschema of allOf.map(
        subschema =>
            ('$ref' in subschema
                ? resolveSchemaRef(subschema.$ref)
                : subschema) as JsonSchemaObject,
    )) {
        Object.assign(properties, subschema.properties);
        if (subschema.required) {
            required.push(...subschema.required);
        }

        if (subschema.discriminator) {
            const { propertyName, mapping } = subschema.discriminator;
            const ref = `#/components/schemas/${name}`;
            const discriminatorValue = Object.entries(mapping).find(entry => entry[1] === ref)![0];
            properties[propertyName] = {
                ...properties[propertyName],
                description: `The discriminator value \`${JSON.stringify(discriminatorValue)}\`.`,
            };
        }
    }

    return { type: 'object', properties, required };
}
